import React, { useState, useEffect, useRef } from 'react'
import { Lock, UploadCloud, CheckCircle2, AlertCircle } from 'lucide-react'
import { CLOUD_NAME, UPLOAD_PRESET } from '../config/cloudinary'

// PIN básico solo para filtrar curiosos que lleguen al enlace por accidente.
// NO es un mecanismo de seguridad real: cualquiera que lea el código fuente puede verlo.
// Cámbialo si se comparte accidentalmente.
const UPLOAD_PIN = '2026GARD'

const WIDGET_SCRIPT_SRC = 'https://upload-widget.cloudinary.com/global/all.js'

const ExpoGardeniasUpload = () => {
  const [pinInput, setPinInput] = useState('')
  const [unlocked, setUnlocked] = useState(false)
  const [pinError, setPinError] = useState(false)
  const [scriptReady, setScriptReady] = useState(false)
  const widgetRef = useRef(null)

  // Carga dinámica del script del widget de Cloudinary
  useEffect(() => {
    if (window.cloudinary) {
      setScriptReady(true)
      return
    }

    const existingScript = document.querySelector(`script[src="${WIDGET_SCRIPT_SRC}"]`)
    if (existingScript) {
      existingScript.addEventListener('load', () => setScriptReady(true))
      return
    }

    const script = document.createElement('script')
    script.src = WIDGET_SCRIPT_SRC
    script.async = true
    script.onload = () => setScriptReady(true)
    document.body.appendChild(script)
  }, [])

  const handlePinSubmit = (e) => {
    e.preventDefault()
    if (pinInput === UPLOAD_PIN) {
      setUnlocked(true)
      setPinError(false)
    } else {
      setPinError(true)
    }
  }

  const openWidget = () => {
    if (!window.cloudinary) return

    if (CLOUD_NAME === 'PENDIENTE_CONFIGURAR' || UPLOAD_PRESET === 'PENDIENTE_CONFIGURAR') {
      alert('La carga de archivos aún no está configurada. Contacta al administrador del sitio.')
      return
    }

    if (!widgetRef.current) {
      widgetRef.current = window.cloudinary.createUploadWidget(
        {
          cloudName: CLOUD_NAME,
          uploadPreset: UPLOAD_PRESET,
          folder: 'expogardenias',
          tags: ['expogardenias'],
          sources: ['local', 'camera'],
          multiple: true,
          maxFiles: 20,
          language: 'es',
        },
        (error, result) => {
          if (error) {
            console.error('Error en el widget de Cloudinary:', error)
          }
        }
      )
    }

    widgetRef.current.open()
  }

  return (
    <div className="pt-32 pb-16 min-h-screen bg-gray-50">
      <div className="container-custom px-4 max-w-lg mx-auto">
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-gardenia-red p-3 rounded-full">
              <UploadCloud className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gardenia-dark">
                Subir contenido a ExpoGardenias
              </h1>
              <p className="text-sm text-gray-500">Acceso exclusivo para docentes</p>
            </div>
          </div>

          {!unlocked ? (
            <form onSubmit={handlePinSubmit} className="space-y-4">
              <label className="block text-sm font-medium text-gardenia-dark">
                Ingresa el PIN de acceso
              </label>
              <div className="relative">
                <Lock className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="password"
                  value={pinInput}
                  onChange={(e) => {
                    setPinInput(e.target.value)
                    setPinError(false)
                  }}
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gardenia-red"
                  placeholder="PIN"
                />
              </div>
              {pinError && (
                <p className="text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" /> PIN incorrecto, intenta de nuevo.
                </p>
              )}
              <button
                type="submit"
                className="w-full bg-gardenia-red hover:bg-gardenia-red/90 text-white font-medium py-2.5 rounded-lg transition-colors"
              >
                Ingresar
              </button>
            </form>
          ) : (
            <div className="space-y-6">
              <div className="bg-gardenia-gold/20 border border-gardenia-gold rounded-lg p-4 text-sm text-gardenia-dark space-y-2">
                <p className="font-semibold flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-gardenia-green" /> Instrucciones
                </p>
                <ul className="list-disc list-inside space-y-1 text-gray-700">
                  <li>Sube fotos (JPG, PNG) o videos cortos (MP4) de los eventos de ExpoGardenias.</li>
                  <li>Puedes seleccionar hasta 20 archivos a la vez.</li>
                  <li>El contenido aparecerá automáticamente en la galería pública en unos minutos.</li>
                  <li>Evita subir archivos con información personal sensible de los estudiantes.</li>
                </ul>
              </div>

              <button
                onClick={openWidget}
                disabled={!scriptReady}
                className="w-full bg-gardenia-green hover:bg-gardenia-green/90 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <UploadCloud className="h-5 w-5" />
                {scriptReady ? 'Abrir selector de archivos' : 'Cargando herramienta...'}
              </button>

              {(CLOUD_NAME === 'PENDIENTE_CONFIGURAR' || UPLOAD_PRESET === 'PENDIENTE_CONFIGURAR') && (
                <p className="text-xs text-gray-400 text-center">
                  Nota para el administrador: falta configurar CLOUD_NAME y UPLOAD_PRESET en
                  src/config/cloudinary.js
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ExpoGardeniasUpload
